import { defineRouting } from 'next-intl/routing';

/**
 * Configuración de routing internacional.
 *
 * - **Locales soportados**: `es-MX` (default), `en-US`.
 * - **Estrategia**: subpath con prefijo `as-needed` — el default (`es-MX`) NO
 *   tiene prefijo en URL (`/colecciones`), `en-US` sí lo tiene (`/en/colecciones`).
 *   Esto preserva las URLs existentes para SEO y simplifica la migración.
 * - **Locale prefix `en`** (no `en-US`) por convención web — más cortos en URL.
 * - **Detección automática**: middleware analiza Accept-Language header en la
 *   primera visita y, si el usuario está en `/`, lo redirige a `/en` o lo deja
 *   como está según el header. La cookie `NEXT_LOCALE` persiste la elección.
 *
 * Docs: https://next-intl.dev/docs/routing
 */
export const routing = defineRouting({
  locales: ['es-MX', 'en-US'],
  defaultLocale: 'es-MX',
  localePrefix: {
    // `as-needed`: el `defaultLocale` NO se prefija; los otros sí.
    // Los `prefixes` solo se declaran para los NO-default — el default
    // queda implicitly sin prefijo. Si declaramos también el default
    // (con `/`) next-intl entra en redirect loop.
    mode: 'as-needed',
    prefixes: {
      'en-US': '/en',
    },
  },
  // Detection: usa Accept-Language para usuarios nuevos
  localeDetection: true,
});

export type Locale = (typeof routing.locales)[number];

/** Convierte el código `es-MX | en-US` a un código BCP-47 corto (`es | en`). */
export function shortLocale(locale: Locale): 'es' | 'en' {
  return locale === 'en-US' ? 'en' : 'es';
}

/** Devuelve el otro idioma (toggle). */
export function otherLocale(locale: Locale): Locale {
  return locale === 'es-MX' ? 'en-US' : 'es-MX';
}
